import { useEffect } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '../config/firebase'
import { useAuthStore } from '../store/authStore'
import type { AuthUser } from '../types/user'

/**
 * Hook yang menginisialisasi dan memantau status autentikasi Firebase.
 * Membaca custom claims dari ID token untuk menentukan role pengguna.
 * Jika custom claims belum terpasang (mis. blocking functions belum dideploy),
 * memverifikasi langsung ke Firestore appConfig/superadmins dan teacherAllowlist.
 * Harus dipanggil SATU KALI di komponen root (App.tsx).
 */
export function useAuthInit() {
  const { setFirebaseUser, setAuthUser, setLoading, setInitialized, setError, reset } = useAuthStore()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true)
      setError(null)

      if (!firebaseUser) {
        reset()
        setInitialized(true)
        setLoading(false)
        return
      }

      try {
        // Paksa refresh token untuk mendapatkan custom claims terbaru
        const idTokenResult = await firebaseUser.getIdTokenResult(true)
        const claims = idTokenResult.claims as { role?: string; isActive?: boolean }

        let role = claims.role as AuthUser['role']
        let isActive = claims.isActive !== false

        const emailLower = (firebaseUser.email || '').toLowerCase()

        // 1. Cek Superadmin jika belum ada role dari claims
        if (!role && emailLower) {
          try {
            const superDoc = await getDoc(doc(db, 'appConfig', 'superadmins'))
            if (superDoc.exists()) {
              const superData = superDoc.data()
              if (superData[firebaseUser.uid] || superData[emailLower]) {
                role = 'superadmin'
                isActive = true
              }
            }
          } catch (superErr) {
            console.warn('[useAuthInit] Superadmin check failed:', superErr)
          }
        }

        // 2. Selalu cek & sinkronisasi data ke teacherAllowlist jika email akun ini terdaftar
        if (emailLower) {
          try {
            const teacherRef = doc(db, 'teacherAllowlist', emailLower)
            const teacherDoc = await getDoc(teacherRef)
            if (teacherDoc.exists()) {
              const tData = teacherDoc.data()

              // Jika belum dapat role (bukan superadmin), pasang role teacher
              if (!role) {
                if (tData.status === 'suspended') {
                  role = 'teacher'
                  isActive = false
                } else if (tData.status !== 'revoked') {
                  role = 'teacher'
                  isActive = true
                }
              }

              // Update displayName & lastLoginAt ke allowlist (jika tidak di-suspend/revoked)
              if (tData.status !== 'suspended' && tData.status !== 'revoked') {
                try {
                  const updates: Record<string, unknown> = {
                    lastLoginAt: serverTimestamp(),
                    displayName: firebaseUser.displayName || tData.displayName || 'Pengguna PRADNYA',
                  }
                  if (tData.status === 'invited') {
                    updates.status = 'active'
                    updates.activatedAt = serverTimestamp()
                  }
                  await updateDoc(teacherRef, updates)
                } catch (updateErr) {
                  console.warn('[useAuthInit] updateDoc teacherAllowlist failed:', updateErr)
                }
              }
            }
          } catch (teacherErr) {
            console.warn('[useAuthInit] Teacher allowlist check failed:', teacherErr)
          }
        }

        setFirebaseUser(firebaseUser)
        setAuthUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email ?? '',
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          role: role ?? null,
          isActive,
        })
      } catch (err) {
        console.error('[useAuthInit] Error membaca token:', err)
        setError('Gagal memverifikasi akun. Coba muat ulang halaman.')
      } finally {
        setInitialized(true)
        setLoading(false)
      }
    })

    return () => unsubscribe()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Hook untuk mengakses state auth di seluruh komponen.
 */
export function useAuth() {
  return useAuthStore()
}
